import React, { FC } from 'react'
import { Link, useParams } from 'react-router-dom'

import { LangValue } from '../../../states/types/common'

import Loading from '../common/Loading'

import { setPath, setUrlId } from '../../../utils/url'
import { URL } from '../../constants/url'

import { UserData, DisplayTime, DisplayBizPoint, MaxUsedCategory, MaxUsedApplication } from '../../../states/types/report'
import { useTranslation } from 'react-i18next'
import { attendanceTimeFormat, convertSecondsToHourAndMinute } from '../../../utils/date'
import { hex2rgb } from '../../../utils/color'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import ReactTooltip from 'react-tooltip'

interface UserDataProps {
  isLoading: boolean | undefined
  userData: UserData[] | UserData | undefined
  mode: "daily" | "monthly" | "custom"
  isTeamManager: boolean | undefined
}

interface displayValues {
  compare: number | undefined
  time?: number
  biz?: number
}

interface displayMaxUsedValues {
  maxUsedCategory: MaxUsedCategory
  maxUsedApplication: MaxUsedApplication
}

const UserData: FC<UserDataProps> = (props) => {
  const { t } = useTranslation()

  const {
    isLoading,
    userData,
    mode,
    isTeamManager,
  } = props

  const { language } = useParams<{ language: LangValue }>()

  const buildMemberReportUrl = (id: number) => setPath(setUrlId(URL.RISK_MEMBER_DETAIL.path, 'id', id), language)

  const buildPositiveNegativeIcon = (value: number) => (value >= 0 ? "/static/images/report/icon_up.svg" : "/static/images/report/icon_down.svg")

  const renderDisplayDetail = (value: displayValues) => (
    <>
      {
        value && (('time' in value && value.time !== undefined && 0 < value.time) || ('biz' in value && value.biz !== undefined && 0 < value.biz)) ? (
          <>
            {
              language === 'en' && (
                <div className="past_text">
                  <>
                    {mode === "monthly" ? t("parts.user_data.comparison_last_month") : t("parts.user_data.comparison_past")}
                  </>
                </div>
              )
            }
            <div className="information">
              {
                value.compare !== undefined && value.compare !== null ? (
                  <>
                    <img className="positive_negative_icon" src={buildPositiveNegativeIcon(value.compare)} alt="" />
                    <span className={value.compare >= 0 ? 'compare_plus' : 'compare_minus'}>
                      {value.compare}
                      %
                    </span>
                  </>
                ) : (
                  <>
                    <span className="compare_none">
                      --
                    </span>
                  </>
                )
              }
              {
                language === 'ja' && (
                  <span className="text">
                    (
                    <>
                      {mode === "monthly" ? t("parts.user_data.comparison_last_month") : t("parts.user_data.comparison_past")}
                    </>
                    )
                  </span>
                )
              }
            </div>
            <div className="value">
              {
                'time' in value && value.time !== undefined ? (
                  <span className="text">
                    {convertSecondsToHourAndMinute(value.time)}
                  </span>
                ) : (
                  <>
                    <span className="text">
                      {value.biz !== undefined && value.biz !== null ? value.biz?.toLocaleString() : 0}
                    </span>
                    &nbsp;
                    biz
                  </>
                )
              }
            </div>
          </>
        ) : (
          <div className="information">
            ---
          </div>
        )
      }
    </>
  )

  // eslint-disable-next-line arrow-body-style
  const renderDisplayWorkingTime = (value: DisplayTime | undefined) => {
    return renderDisplayDetail({ compare: value?.work_time_compare, time: value?.work_time })
  }

  // eslint-disable-next-line arrow-body-style
  const renderDisplayActiveTime = (value: DisplayTime | undefined) => {
    return renderDisplayDetail({ compare: value?.active_time_compare, time: value?.active_time })
  }

  // eslint-disable-next-line arrow-body-style
  const renderDisplayBizPoint = (value: DisplayBizPoint | undefined) => {
    return renderDisplayDetail({ compare: value?.compare, biz: value?.target })
  }

  const renderDisplayAttendance = (value: DisplayTime | undefined) => (
    <>
      {
        value && value?.end_time !== undefined && value.end_time !== null ? (
          <>
            <div className="information" lang={language}>
              <span className="text">
                操作開始・終了
              </span>
            </div>
            <div className="value">
              <span className="text">
                {attendanceTimeFormat(value?.start_time)}
                &nbsp;-&nbsp;
                {attendanceTimeFormat(value?.end_time)}
              </span>
            </div>
          </>
        ) : (
          <div className="information">
            ---
          </div>
        )
      }
    </>
  )

  const renderDisplayMaxUsedCategory = (value: displayMaxUsedValues) => {
    if (value.maxUsedCategory) {
      return (
        <div
          className="category_box"
          style={{ ['--color' as string]: hex2rgb(value.maxUsedCategory.category_color) }}
        >
          <div className="title">
            <img
              src={`/static/images/report/icon_category_${value.maxUsedCategory.category_icon}.svg`}
              alt={value.maxUsedCategory.category_icon}
            />
            {t(`parts.category_name.${value.maxUsedCategory.name}`)}
          </div>
          <div className="detail">
            {value.maxUsedApplication.target_name}
          </div>
        </div>
      )
    }
    return (
      <>
        ---
      </>
    )
  }

  const renderUserDataBody = (item: UserData) => (
    <React.Fragment key={item.id}>
      <tr className="manager">
        <td className="table-body">
          <p className="table-body-name">
            {
              item.profile_image ? (
                <img alt="profile" src={item.profile_image} className="profile_icon" />
              ) : (
                <img
                  alt=""
                  src={`/static/images/profile/${(item.id || 0) % 12}.jpeg`}
                  className="profile_icon"
                />
              )
            }
            {
                isTeamManager === true ? (
                  <Link to={buildMemberReportUrl(item.id)}>{item.name}</Link>
                ) : (
                  <>{item.name}</>
                )
              }
          </p>
        </td>
        {
          mode === "daily" && (
            <td className="table-body">
              { renderDisplayAttendance(item.working_time) }
            </td>
          )
        }
        <td className="table-body">
          { renderDisplayWorkingTime(item.working_time) }
        </td>
        <td className="table-body">
          { renderDisplayActiveTime(item.working_time) }
        </td>
        <td className="table-body">
          { renderDisplayBizPoint(item.biz_point) }
        </td>
        <td className="table-body">
          { renderDisplayMaxUsedCategory({ maxUsedCategory: item?.max_used_category, maxUsedApplication: item?.max_used_application }) }
        </td>
      </tr>
    </React.Fragment>
  )

  return (
    <div style={{ marginLeft: "0.5em", marginRight: "0.5em" }}>
      <div className="box">
        {
          isLoading || !userData ? (
            <Loading />
          ) : (
            <table className={`user_data_table ${mode}`}>
              <thead>
                <tr>
                  <th className="table-header">
                    {t('parts.user_data.name')}
                  </th>
                  {
                    mode === "daily" && (
                      <th className="table-header">
                        操作開始・終了時間
                      </th>
                    )
                  }
                  <th className="table-header">
                    {t('parts.user_data.work_time')}
                    <span className="info_icon" data-html="true" data-tip={t('parts.user_data.work_time_description')}>
                      <FontAwesomeIcon icon={['far', 'question-circle']} />
                      <ReactTooltip place="bottom" effect="solid" />
                    </span>
                  </th>
                  <th className="table-header">
                    {t('parts.user_data.active_time')}
                  </th>
                  <th className="table-header">
                    {t('parts.user_data.biz')}
                  </th>
                  <th className="table-header">
                    {t('parts.user_data.main_task')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {
                  userData && (
                    'length' in userData ? (
                      userData?.map((item) => renderUserDataBody(item))
                    ) : (
                      renderUserDataBody(userData)
                    )
                  )
                }
              </tbody>
            </table>
          )
        }
      </div>
    </div>
  )
}

export default UserData
